import { Theme, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { Page } from '@/components/Layout/Page/Page';
import { OnboardingGuide } from '@/components/OnboardingGuide';

import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { ApplicationType } from '@/types/user';

import {
  StyledBackButton,
  StyledComingSoonPage,
  StyledContent,
} from './ComingSoonPage.styled';

type ComingSoonPageProps = Readonly<{
  type: ApplicationType;
}>;

export default function ComingSoonPage({ type }: ComingSoonPageProps) {
  const { t, translateIfExists } = useTranslate();
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();

  const handleBack = () => navigate(-1);

  const pageContent = useMemo(() => {
    switch (type) {
      case ApplicationType.BUSINESS:
        return {
          title: t('comingSoon.business.title'),
          message: t('comingSoon.business.message'),
        };
      case ApplicationType.SHARED_WITH_SOMEONE_ELSE:
        return {
          title: t('comingSoon.sharedWithSomeoneElse.title'),
          message: t('comingSoon.sharedWithSomeoneElse.message'),
        };
      case ApplicationType.JOINT: {
        const title = translateIfExists('comingSoon.rightStart.title', {
          defaultValue: '',
        });
        return {
          title,
          message: t('comingSoon.rightStart.message'),
        };
      }
      default:
        return {
          title: '',
          message: '',
        };
    }
  }, [type, t, translateIfExists]);

  return (
    <Page
      paperProps={{
        color: (theme: Theme) => theme.palette.neutral[1000],
        position: 'right',
        size: 'big',
        children: isDesktop ? <OnboardingGuide /> : null,
      }}
      hasLogo
    >
      <StyledComingSoonPage
        direction="column"
        justifyContent="center"
        flex={1}
        spacing={4}
      >
        <StyledContent>
          <Typography variant="h3">{pageContent.title}</Typography>
          <Typography variant="body2">{pageContent.message}</Typography>
        </StyledContent>
        <StyledBackButton
          variant={isDesktop ? 'text' : 'contained'}
          onClick={handleBack}
        >
          {isDesktop ? `← ${t('common.button.back')}` : t('common.button.back')}
        </StyledBackButton>
      </StyledComingSoonPage>
    </Page>
  );
}
